import { Transaction } from '@api/hooks/types/getTransactions';
import { useProofOfPayment } from '@api/hooks/useTransactions';
import { PermissionGate } from '@components/PermissionGate';
import { Button, DownloadIcon, PlusIcon } from '@gurupay/gp-ui-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { OperationStatus, PAYMENT_TYPE } from 'types/operations';
import { Capability } from 'types/roles';

const paymentUrlMapper = {
	[PAYMENT_TYPE.DOMESTIC]: 'sepa',
	[PAYMENT_TYPE.INTERNAL]: 'sepa',
	[PAYMENT_TYPE.INTERNATIONAL]: 'international',
	[PAYMENT_TYPE.FX]: 'fx',
	[PAYMENT_TYPE.BATCH]: 'batch',
};

interface ActionsProps {
	operation: Transaction;
}

const Actions = ({ operation }: ActionsProps) => {
	const { t } = useTranslation('', { keyPrefix: 'payments.specificPayment' });
	const proofOfPayment = useProofOfPayment(operation?.id);
	const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
	const [isDownloading, setIsDownloading] = useState(false);

	const paymentTypeUrl: string = operation?.payment_type
		? paymentUrlMapper[operation.payment_type as PAYMENT_TYPE]
		: 'sepa';

	useEffect(() => {
		if (proofOfPayment.isSuccess && isDownloading) {
			const blob = new Blob([proofOfPayment.data], { type: 'application/pdf' });
			const url = window.URL.createObjectURL(blob);

			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `${operation?.document_number}.pdf`);

			document.body.appendChild(link);
			link.click();

			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(url);
			setIsDownloading(false);
		}
	}, [proofOfPayment.data, isDownloading, proofOfPayment.isSuccess]);

	return (
		<div className="flex gap-space-8">
			<PermissionGate capability={Capability.CREATE_PAYMENT}>
				<Button
					href={`/payments/${paymentTypeUrl}/${operation?.id}`}
					className="flex-row-reverse"
					icon={<PlusIcon />}
					size={isMobile ? 'sm' : 'md'}
					variant="secondary"
				>
					{t('actions.payAgain')}
				</Button>
			</PermissionGate>

			{operation.status === OperationStatus.EXECUTED && (
				<Button
					className="flex-row-reverse"
					disabled={isDownloading}
					icon={<DownloadIcon />}
					onClick={() => {
						setIsDownloading(true);
						proofOfPayment.refetch();
					}}
					size={isMobile ? 'sm' : 'md'}
					variant="secondary"
				>
					{t('actions.download')}
				</Button>
			)}
		</div>
	);
};

export default Actions;
